// frontend/app/components/admin/LessonStepFields.js
//
// One step of the lesson: its own text, then its blocks.
//
// Collapsed until opened. A lesson runs to seven steps and forty blocks, and
// an admin who came here to fix one sentence should not have to scroll past
// the other thirty-nine to find it.
//
// The blocks are edited and added by the authoring BlockEditor and
// BlockPalette, not by a second editor written for this panel. Both are driven
// entirely by blockSchemas, so a block type added there stays editable — and
// creatable — here without anyone remembering to come back.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import BlockEditor from "../lessonPlans/authoring/BlockEditor";
import BlockPalette from "../lessonPlans/authoring/BlockPalette";
import { Field, TextField } from "../lessonPlans/authoring/WizardFields";
import { getPhaseMeta } from "../lessonPlans/lessonPlanTheme";

const LessonStepFields = ({
  colors,
  step,
  index,
  expanded,
  onToggle,
  onChange,
  onBlockAdd,
  onBlockChange,
  onBlockRemove,
  onBlockMove,
  concepts = [],
  boardTopicId,
  catalog,
}) => {
  const set = (fields) => onChange(fields);
  const phase = getPhaseMeta(step.phase);
  const blocks = Array.isArray(step.blocks) ? step.blocks : [];

  return (
    <View
      style={[styles.card, { backgroundColor: colors.panelBg, borderColor: colors.border }]}
    >
      <TouchableOpacity
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`Step ${index + 1}: ${step.title || "untitled"}`}
        style={styles.header}
      >
        <View style={[styles.seq, { backgroundColor: colors.accentGlow }]}>
          <Text style={[styles.seqText, { color: colors.accentPrimary }]}>{index + 1}</Text>
        </View>

        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={[styles.title, { color: colors.textPrimary }]} numberOfLines={1}>
            {step.title || "Untitled step"}
          </Text>
          <Text style={[styles.meta, { color: colors.textMuted }]} numberOfLines={1}>
            {phase ? `${phase.label} · ` : ""}
            {blocks.length} block{blocks.length === 1 ? "" : "s"}
            {step.instructional_mode ? ` · ${step.instructional_mode.replace("_", " ")}` : ""}
          </Text>
        </View>

        <MaterialCommunityIcons
          name={expanded ? "chevron-up" : "chevron-down"}
          size={20}
          color={colors.textMuted}
        />
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.body}>
          <Field colors={colors} label="Step title" required>
            <TextField
              colors={colors}
              value={step.title}
              onChange={(title) => set({ title })}
              placeholder="What question opens this step?"
              accessibilityLabel={`Step ${index + 1} title`}
            />
          </Field>

          <Field colors={colors} label="Step subtitle">
            <TextField
              colors={colors}
              value={step.subtitle}
              onChange={(subtitle) => set({ subtitle })}
              placeholder="One line under the title."
              accessibilityLabel={`Step ${index + 1} subtitle`}
            />
          </Field>

          <Field
            colors={colors}
            label="Teacher guidance"
            hint="Only the teacher sees this."
          >
            <TextField
              colors={colors}
              value={step.teacher_guidance}
              onChange={(teacher_guidance) => set({ teacher_guidance })}
              placeholder="What to say, what to watch for."
              accessibilityLabel={`Step ${index + 1} teacher guidance`}
              multiline
            />
          </Field>

          <Field colors={colors} label="What students do">
            <TextField
              colors={colors}
              value={step.student_activity}
              onChange={(student_activity) => set({ student_activity })}
              placeholder="Observe the model and name the structures."
              accessibilityLabel={`Step ${index + 1} student activity`}
              multiline
            />
          </Field>

          <Field colors={colors} label="Minutes">
            <TextField
              colors={colors}
              value={step.estimated_minutes}
              onChange={(estimated_minutes) => set({ estimated_minutes })}
              placeholder="8"
              keyboardType="numeric"
              accessibilityLabel={`Step ${index + 1} minutes`}
            />
          </Field>

          <Text style={[styles.blocksHeading, { color: colors.textSecondary }]}>
            Content in this step
          </Text>

          {blocks.length ? (
            blocks.map((block, blockIndex) => (
              <BlockEditor
                key={block.id || `block-${blockIndex}`}
                colors={colors}
                block={block}
                concepts={concepts}
                boardTopicId={boardTopicId}
                catalog={catalog}
                onChange={(next) => onBlockChange(blockIndex, next)}
                onRemove={() => onBlockRemove(blockIndex)}
                onMove={(delta) => onBlockMove(blockIndex, delta)}
                isFirst={blockIndex === 0}
                isLast={blockIndex === blocks.length - 1}
              />
            ))
          ) : (
            <Text style={[styles.meta, { color: colors.textMuted }]}>
              This step has no blocks yet. Add one below.
            </Text>
          )}

          <Text style={[styles.blocksHeading, { color: colors.textSecondary }]}>
            Add to this step
          </Text>

          {/* The wizard's own palette, so the two screens cannot drift apart on
              what a lesson may contain. `stepPhase` is what makes it say when a
              type belongs somewhere else in the Loop. */}
          <BlockPalette colors={colors} stepPhase={step.phase} onAdd={onBlockAdd} />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: { borderRadius: 13, borderWidth: 1, marginBottom: 9, overflow: "hidden" },
  header: { flexDirection: "row", alignItems: "center", gap: 10, padding: 12 },
  seq: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  seqText: { fontSize: 11, fontWeight: "800" },
  title: { fontSize: 13, fontWeight: "700" },
  meta: { fontSize: 11, marginTop: 2 },

  body: { paddingHorizontal: 14, paddingBottom: 14 },
  blocksHeading: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginTop: 4,
    marginBottom: 10,
  },
});

export default LessonStepFields;
